import { createTheme, Theme } from "@mui/material/styles";

// override modules for material ui can be found in modules/mui-overrides.d.ts

declare module "@mui/material/Accordion" {
  interface AccordionPropsVariantOverrides {
    borderless: true;
  }
}

declare module "@mui/material/Paper" {
  interface PaperPropsVariantOverrides {
    borderless: true;
  }
}

// Theme augmentation for shadow elevation
declare module "@mui/material/styles" {
  interface Theme {
    elevation: string[];
  }
  interface ThemeOptions {
    elevation?: string[];
  }
}

let base = createTheme({});

const baseTheme: Theme = createTheme({
  typography: {
    fontFamily: "AlbertSans, SourceSerifPro, sans-serif",
    display: {
      fontFamily: "SourceSerifPro",
      [base.breakpoints.down("sm")]: {
        fontSize: "28px",
      },
      fontSize: "32px",
      fontWeight: "600",
      color: "#3D3D3D",
      lineHeight: "normal",
      letterSpacing: "-0.32px",
    },
    title: {
      fontFamily: "SourceSerifPro",
      [base.breakpoints.down("sm")]: {
        fontSize: "24px",
      },
      fontSize: "28px",
      fontWeight: "600",
      color: "#3D3D3D",
      lineHeight: "normal",
      letterSpacing: "-0.32px",
    },
    h1: {
      fontFamily: "AlbertSans",
      fontSize: "28px",
      fontWeight: "700",
      color: "#3D3D3D",
      lineHeight: "1.3",
      letterSpacing: "-0.4px",
    },
    h1Serif: {
      fontFamily: "SourceSerifPro",
      fontSize: "28px",
      fontWeight: "600",
      color: "#3D3D3D",
      lineHeight: "1.3",
      letterSpacing: "-0.32px",
    },
    h2: {
      fontFamily: "AlbertSans",
      fontSize: "24px",
      fontWeight: "700",
      color: "#3D3D3D",
      lineHeight: "1.3",
      letterSpacing: "-0.4px",
    },
    h2Serif: {
      fontFamily: "SourceSerifPro",
      fontSize: "24px",
      fontWeight: "600",
      color: "#3D3D3D",
      lineHeight: "22px",
      letterSpacing: "-0.32px",
    },
    h3: {
      fontFamily: "AlbertSans",
      fontSize: "20px",
      fontWeight: "700",
      color: "#3D3D3D",
      lineHeight: "1.3",
      letterSpacing: "-0.4px",
    },
    h3Serif: {
      fontFamily: "SourceSerifPro",
      fontSize: "20px",
      fontWeight: "600",
      color: "#3D3D3D",
      lineHeight: "22px",
      letterSpacing: "-0.32px",
    },
    h4: {
      fontFamily: "AlbertSans",
      fontSize: "18px",
      fontWeight: "700",
      color: "#3D3D3D",
      lineHeight: "130%",
      letterSpacing: "-0.4px",
    },
    h4Serif: {
      fontFamily: "SourceSerifPro",
      fontSize: "18px",
      fontWeight: "600",
      color: "#3D3D3D",
      lineHeight: "130%",
      letterSpacing: "-0.32px",
      display: "block", // Line height behaves differently on display block/inline block.  Material renders typography as spans.
    },
    h5: {
      fontFamily: "AlbertSans",
      fontSize: "16px",
      fontWeight: "600",
      color: "#3D3D3D",
      lineHeight: "130%",
      letterSpacing: "-0.4px",
    },
    body1: {
      fontFamily: "AlbertSans",
      fontSize: "15px",
      fontWeight: "500",
      lineHeight: "141%",
      letterSpacing: "-0.4px",
      display: "block", // Line height behaves differently on display block/inline block.  Material renders typography as spans.
    },
    body: {
      fontFamily:
        "AlbertSans, 'Apple Color Emoji', 'Segoe UI Emoji', 'Noto Color Emoji', sans-serif",
      fontSize: "15px",
      fontWeight: "500",
      color: "#3D3D3D",
      lineHeight: "141%",
      letterSpacing: "-0.4px",
      display: "block", // Line height behaves differently on display block/inline block.  Material renders typography as spans.
    },
    bodyHeavy: {
      fontFamily: "AlbertSans",
      fontSize: "15px",
      fontWeight: "600",
      color: "#3D3D3D",
      lineHeight: "141%",
      letterSpacing: "-0.4px",
      display: "block", // Line height behaves differently on display block/inline block.  Material renders typography as spans.
    },
    bodySmall: {
      fontFamily: "AlbertSans",
      fontSize: "14px",
      fontWeight: "500",
      color: "#3D3D3D",
      lineHeight: "140%",
      letterSpacing: "-0.4px",
      display: "block", // Line height behaves differently on display block/inline block.  Material renders typography as spans.
    },
    bodySmallHeavy: {
      fontFamily: "AlbertSans",
      fontSize: "14px",
      fontWeight: "700",
      color: "#3D3D3D",
      lineHeight: "140%",
      letterSpacing: "-0.4px",
      display: "block", // Line height behaves differently on display block/inline block.  Material renders typography as spans.
    },
    caption: {
      fontFamily: "AlbertSans",
      fontSize: "15px",
      fontWeight: "500",
      color: "#3D3D3D",
      lineHeight: "140%",
      letterSpacing: "-0.4px",
      display: "block", // Line height behaves differently on display block/inline block.  Material renders typography as spans.
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        @font-face {
          font-family: 'AlbertSans';
          src: url('/assets/fonts/AlbertSans-VariableFont_wght.ttf') format('truetype');
          /* Add more src declarations for different font formats if available */
          font-weight: 100 1000;
          font-style: normal;
        }
        @font-face{
          font-family: 'SourceSerifPro';
          font-weight: 200;
          font-style: normal;
          font-stretch: normal;
          src: url('/assets/fonts/SourceSerifPro/WOFF2/TTF/SourceSerifPro-ExtraLight.ttf.woff2') format('woff2'),
               url('/assets/fonts/SourceSerifPro/WOFF/OTF/SourceSerifPro-ExtraLight.otf.woff') format('woff');
      }

      @font-face{
          font-family: 'SourceSerifPro';
          font-weight: 200;
          font-style: italic;
          font-stretch: normal;
          src: url('/assets/fonts/SourceSerifPro/WOFF2/TTF/SourceSerifPro-ExtraLightIt.ttf.woff2') format('woff2'),
               url('/assets/fonts/SourceSerifPro/WOFF/OTF/SourceSerifPro-ExtraLightIt.otf.woff') format('woff');
      }

      @font-face{
          font-family: 'SourceSerifPro';
          font-weight: 300;
          font-style: normal;
          font-stretch: normal;
          src: url('/assets/fonts/SourceSerifPro/WOFF2/TTF/SourceSerifPro-Light.ttf.woff2') format('woff2'),
               url('/assets/fonts/SourceSerifPro/WOFF/OTF/SourceSerifPro-Light.otf.woff') format('woff');
      }

      @font-face{
          font-family: 'SourceSerifPro';
          font-weight: 300;
          font-style: italic;
          font-stretch: normal;
          src: url('/assets/fonts/SourceSerifPro/WOFF2/TTF/SourceSerifPro-LightIt.ttf.woff2') format('woff2'),
               url('/assets/fonts/SourceSerifPro/WOFF/OTF/SourceSerifPro-LightIt.otf.woff') format('woff');
      }

      @font-face{
          font-family: 'SourceSerifPro';
          font-weight: 400;
          font-style: normal;
          font-stretch: normal;
          src: url('/assets/fonts/SourceSerifPro/WOFF2/TTF/SourceSerifPro-Regular.ttf.woff2') format('woff2'),
               url('/assets/fonts/SourceSerifPro/WOFF/OTF/SourceSerifPro-Regular.otf.woff') format('woff');
      }

      @font-face{
          font-family: 'SourceSerifPro';
          font-weight: 400;
          font-style: italic;
          font-stretch: normal;
          src: url('/assets/fonts/SourceSerifPro/WOFF2/TTF/SourceSerifPro-It.ttf.woff2') format('woff2'),
               url('/assets/fonts/SourceSerifPro/WOFF/OTF/SourceSerifPro-It.otf.woff') format('woff');
      }

      @font-face{
          font-family: 'SourceSerifPro';
          font-weight: 600;
          font-style: normal;
          font-stretch: normal;
          src: url('/assets/fonts/SourceSerifPro/WOFF2/TTF/SourceSerifPro-Semibold.ttf.woff2') format('woff2'),
               url('/assets/fonts/SourceSerifPro/WOFF/OTF/SourceSerifPro-Semibold.otf.woff') format('woff');
      }

      @font-face{
          font-family: 'SourceSerifPro';
          font-weight: 600;
          font-style: italic;
          font-stretch: normal;
          src: url('/assets/fonts/SourceSerifPro/WOFF2/TTF/SourceSerifPro-SemiboldIt.ttf.woff2') format('woff2'),
               url('/assets/fonts/SourceSerifPro/WOFF/OTF/SourceSerifPro-SemiboldIt.otf.woff') format('woff');
      }

      @font-face{
          font-family: 'SourceSerifPro';
          font-weight: 700;
          font-style: normal;
          font-stretch: normal;
          src: url('/assets/fonts/SourceSerifPro/WOFF2/TTF/SourceSerifPro-Bold.ttf.woff2') format('woff2'),
               url('/assets/fonts/SourceSerifPro/WOFF/OTF/SourceSerifPro-Bold.otf.woff') format('woff');
      }

      @font-face{
          font-family: 'SourceSerifPro';
          font-weight: 700;
          font-style: italic;
          font-stretch: normal;
          src: url('/assets/fonts/SourceSerifPro/WOFF2/TTF/SourceSerifPro-BoldIt.ttf.woff2') format('woff2'),
               url('/assets/fonts/SourceSerifPro/WOFF/OTF/SourceSerifPro-BoldIt.otf.woff') format('woff');
      }

      @font-face{
          font-family: 'SourceSerifPro';
          font-weight: 900;
          font-style: normal;
          font-stretch: normal;
          src: url('/assets/fonts/SourceSerifPro/WOFF2/TTF/SourceSerifPro-Black.ttf.woff2') format('woff2'),
               url('/assets/fonts/SourceSerifPro/WOFF/OTF/SourceSerifPro-Black.otf.woff') format('woff');
      }

      @font-face{
          font-family: 'SourceSerifPro';
          font-weight: 900;
          font-style: italic;
          font-stretch: normal;
          src: url('/assets/fonts/SourceSerifPro/WOFF2/TTF/SourceSerifPro-BlackIt.ttf.woff2') format('woff2'),
               url('/assets/fonts/SourceSerifPro/WOFF/OTF/SourceSerifPro-BlackIt.otf.woff') format('woff');
      }
      `,
    },
    MuiButtonGroup: {
      styleOverrides: {
        root: {
          backgroundColor: "#F6F6F6",
          borderRadius: "100px",
          overflow: "hidden",
          padding: "2px 2px",
          display: "flex",
        },
      },
    },
    MuiAccordion: {
      variants: [
        {
          props: { variant: "borderless" },
          style: {
            background: "unset",
            boxShadow: "none",
            border: "none",
            padding: "8px 0",
            "& .MuiAccordionSummary-root": {
              padding: "0px",
              gap: "8px",
              justifyContent: "start",
            },
            "& .MuiAccordionSummary-content": {
              margin: "8px 0",
              flexGrow: 0,
              "&.Mui-expanded": {
                margin: "12px 0",
              },
            },
            "& .MuiAccordionDetails-root": {
              padding: "0",
            },
          },
        },
      ],
      styleOverrides: {
        root: {
          boxShadow: "none",
          border: "1px solid #D4D4D4",

          "&:first-of-type": {
            borderTopLeftRadius: "12px",
            borderTopRightRadius: "12px",
          },
          "&:last-of-type": {
            borderBottomLeftRadius: "12px",
            borderBottomRightRadius: "12px",
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          minHeight: "unset",
          "&.Mui-expanded": {
            minHeight: "unset",
          },
        },
        content: {
          margin: "16px 0",
          "&.Mui-expanded": {
            margin: "16px 0 8px",
            minHeight: "unset",
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: "0 16px 16px",
        },
      },
    },
    MuiAlert: {
      styleOverrides: { root: { borderRadius: "12px" } },
      variants: [
        {
          props: { severity: "error" },
          style: {
            backgroundColor: "#FDE7DD",
            color: "#AE4845",
            borderRadius: "12px",
            fontSize: "14px",
            fontWeight: "500",
          },
        },
      ],
    },
    MuiButton: {
      defaultProps: {
        variant: "contained",
        disableRipple: true,
      },
      variants: [
        {
          props: { color: "error" },
          style: {
            borderColor: "#C14743",
            "&:hover": {
              backgroundColor: "#C14743",
              borderColor: "#C14743",
            },
          },
        },
        {
          props: { color: "info" },
          style: {
            borderColor: "rgba(2, 136, 209, 0.5)",
            "&:hover": {
              borderColor: "rgba(2, 136, 209, 0.5)",
            },
          },
        },
        {
          props: { variant: "inline" },
          style: {
            color: "#198282",
            fontWeight: "600",
            height: "auto",
            "&:hover": {
              backgroundColor: "inherit",
            },
          },
        },
        {
          props: { variant: "buttonGroup" },
          style: {
            color: "#3D3D3D",
            border: "none",
            fontWeight: "500",
            borderRadius: "100px !important",
            height: "100%",
            "&:hover": {
              border: "none",
              backgroundColor: "inherit",
            },
            "& .MuiTouchRipple-root": {
              color: "transparent !important",
            },
          },
        },
        {
          props: { variant: "buttonGroupSelected" },
          style: {
            color: "#3D3D3D",
            backgroundColor: "#FFF",
            height: "100%",
            fontWeight: "500",
            borderRadius: "100px !important",
            boxShadow: "0px 1px 6px 0px rgba(0, 0, 0, 0.15)",
            "&:hover": {
              border: "none",
              borderRadius: "100px !important",
              boxShadow: "0px 1px 6px 0px rgba(0, 0, 0, 0.15)",
              backgroundColor: "#FFF",
            },
            "& .MuiTouchRipple-root": {
              color: "transparent !important",
            },
          },
        },
      ],
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: "100px",
          textTransform: "none",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
          "& .MuiButton-endIcon": {
            marginRight: "0px !important",
            marginLeft: "4px !important",
          },
          "&.MuiButton-outlined": {
            "&:active": {
              backgroundColor: "#C8DBD7",
            },
          },
          "&.MuiButton-contained": {
            "&:active": {
              backgroundColor: "#3C5556",
            },
          },
          "&.Mui-disabled": {
            backgroundColor: "#FAF9FA",
          },
        },
        // This is our "Primary button"
        // We only have two sizes, primary/small.
        sizeLarge: {
          height: "56px",
          padding: "8px 32px",
          fontSize: "15px",
          fontWeight: "600",
        },
        sizeMedium: {
          height: "44px",
          padding: "6px 20px",
          fontSize: "15px",
          fontWeight: "600",
        },
        sizeSmall: {
          height: "40px",
          padding: "4px 16px",
          fontSize: "14px",
          fontWeight: "600",
          lineHeight: "normal",
          minWidth: "unset",
        },
        // This maps to primary in the design
        contained: {
          "&:hover": {
            backgroundColor: "#198282",
          },
        },
        // This maps to secondary in the design
        outlined: {
          border: "1.5px solid #198282",
          "&:hover": {
            border: "1.5px solid #198282",
            backgroundColor: "inherit",
          },
        },
        // This maps to tertiary in the design
        text: {
          padding: "0px !important",
          "&:hover": {
            backgroundColor: "transparent",
          },
          "&.Mui-disabled": {
            backgroundColor: "transparent",
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          padding: "4px 8px 4px 0",
          "&:hover": {
            backgroundColor: "transparent",
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          padding: "4px 8px 4px 0",
          "&:hover": {
            backgroundColor: "transparent",
          },
        },
      },
    },

    MuiChip: {
      variants: [
        {
          props: { variant: "success" },
          style: {
            backgroundColor: "#EAF2EA",
            color: "#468E3E",
            "& .MuiChip-icon": { color: "#468E3E" },
          },
        },
        {
          props: { variant: "green" },
          style: {
            backgroundColor: "#EAF2EA",
            color: "#468E3E",
            "& .MuiChip-icon": { color: "#468E3E" },
          },
        },
        {
          props: { variant: "tealDark" },
          style: {
            backgroundColor: "#198282",
            color: "#FFF",
            "& .MuiChip-icon": { color: "#FFF" },
          },
        },
        {
          props: { variant: "teal" },
          style: {
            backgroundColor: "#E9F3F3",
            color: "#198282",
            "& .MuiChip-icon": { color: "#198282" },
          },
        },
        {
          props: { variant: "warning" },
          style: {
            backgroundColor: "#FBF2E5F2",
            color: "#9A6518",
            "& .MuiChip-icon": { color: "#9A6518" },
          },
        },
        {
          props: { variant: "orange" },
          style: {
            backgroundColor: "#FBF2E5F2",
            color: "#9A6518",
            "& .MuiChip-icon": { color: "#9A6518" },
          },
        },
        {
          props: { variant: "error" },
          style: {
            color: "#C14743",
            backgroundColor: "#FCDBCD",
            "& .MuiChip-icon": { color: "#C14743" },
          },
        },
        {
          props: { variant: "red" },
          style: {
            color: "#C14743",
            backgroundColor: "#FCDBCD",
            "& .MuiChip-icon": { color: "#C14743" },
          },
        },
        {
          props: { variant: "gray" },
          style: {
            color: "#6B6E7B",
            backgroundColor: "#F5F5F5",
            "& .MuiChip-icon": { color: "#6B6E7B" },
          },
        },
        {
          props: { variant: "outlined" },
          style: {
            color: "#6B6E7B",
            backgroundColor: "#FFF",
            border: "1px solid #D4D4D4",
            "& .MuiChip-icon": { color: "#6B6E7B" },
          },
        },
        {
          props: { variant: "text" },
          style: {
            backgroundColor: "transparent",
            color: "#6B6E7B",
            border: "none",
            boxShadow: "none",
            "& .MuiChip-icon": { color: "#6B6E7B" },
          },
        },
      ],
      styleOverrides: {
        root: {
          // NOTE(Kip): Undoing this until we can get to the bottom of what should be standard
          // and what should be changed adhoc.
          // backgroundColor: "white",
          // color: "#6B6E7B",
          padding: "16px 6px",
          gap: "4px",
          borderColor: "#D0D5DD",
          alignItems: "center",

          "& .MuiChip-deleteIcon": {
            color: "#8E9598",
            width: "20px",
            height: "20px",
          },

          "& .MuiChip-label": {
            fontWeight: "600",
            fontSize: "15px",
          },
        },
        sizeSmall: {
          padding: "14px 4px",
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          marginTop: "8px",
          padding: "4px 0",
          borderRadius: "12px !important",
          boxShadow: "rgba(34, 32, 29, 0.15) 0px 1px 4px",
          border: "1px solid #F2F2F2",
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          padding: "0",
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          padding: "8px 20px",
          fontWeight: "500",
          "&.Mui-selected": {},
          "&.Mui-focused": {
            backgroundColor: "white",
          },
          "&:hover": {
            backgroundColor: "white",
          },
          "&.Mui-focusVisible": {
            backgroundColor: "transparent !important",
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: "transparent",
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          padding: "0px",

          "&:hover": {
            "@media (hover: none)": {
              backgroundColor: "transparent",
            },
            backgroundColor: "transparent",
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: "24px !important",
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontFamily: "AlbertSans",
          fontSize: "15px",
          color: "#6B6E7B",
          fontWeight: 500,
          lineHeight: "22px",
          top: "18px",
          left: "20px",
          transform: "None",
          "&.Mui-focused": {
            // All these need to be repeated to override
            fontFamily: "AlbertSans",
            color: "#6B6E7B",
            fontWeight: 500,
            fontSize: "12px",
            top: "6px",
            transform: "None",
          },
          "&.MuiInputLabel-filled": {
            fontFamily: "AlbertSans",
            color: "#6B6E7B",
            fontWeight: 500,
            fontSize: "12px",
            top: "6px",
            left: "20px",
          },
          "&.MuiInputLabel-shrink": {
            fontFamily: "AlbertSans",
            color: "#6B6E7B",
            fontWeight: 500,
            fontSize: "12px",
            top: "6px",
            transform: "None",
            left: "20px",
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          minHeight: "60px",
          padding: "0px",
          paddingRight: "12px",
          fontWeight: 500,
          background: "white",
          "& fieldset": {
            borderRadius: "12px",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            border: "1px solid #D4D4D4",
            fontWeight: 500,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            border: "1px solid #D4D4D4",
            fontWeight: 500,
          },
        },
        input: {
          marginTop: "16px",
          padding: "0px 20px",
          paddingRight: "0px",
          fontWeight: 500,
          fontSize: "14px",
          "@supports (-webkit-touch-callout: none)": {
            "@media (max-width: 768px)": {
              fontSize: "16px",
            },
          },
        },
        inputMultiline: {
          marginTop: "28px",
          marginBottom: "6px",
          fontWeight: 500,
          "@supports (-webkit-touch-callout: none)": {
            "@media (max-width: 768px)": {
              fontSize: "16px",
            },
          },
        },
        notchedOutline: {
          top: "0px",
          border: "1px solid #D4D4D4",
          borderRadius: "8px",
          fontWeight: 500,
          "& legend": {
            display: "none",
          },
        },
      },
    },

    MuiInputBase: {
      styleOverrides: {
        input: {
          "@supports (-webkit-touch-callout: none)": {
            "@media (max-width: 768px)": {
              fontSize: "16px",
            },
          },
        },
        inputMultiline: {
          "@supports (-webkit-touch-callout: none)": {
            "@media (max-width: 768px)": {
              fontSize: "16px",
            },
          },
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        input: {
          "@supports (-webkit-touch-callout: none)": {
            "@media (max-width: 768px)": {
              fontSize: "16px",
            },
          },
        },
        inputMultiline: {
          "@supports (-webkit-touch-callout: none)": {
            "@media (max-width: 768px)": {
              fontSize: "16px",
            },
          },
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        input: {
          "@supports (-webkit-touch-callout: none)": {
            "@media (max-width: 768px)": {
              fontSize: "16px",
            },
          },
        },
        inputMultiline: {
          "@supports (-webkit-touch-callout: none)": {
            "@media (max-width: 768px)": {
              fontSize: "16px",
            },
          },
        },
      },
    },

    MuiTooltip: {
      defaultProps: {
        arrow: true,
        placement: "top",
      },
      styleOverrides: {
        tooltip: {
          backgroundColor: "white",
          color: "#3D3D3D",
          fontSize: "13px",
          border: "1px solid #D4D4D4",
          boxShadow: "0px 2px 8px 0px rgba(151, 93, 51, 0.07)",
          borderRadius: "8px",
          margin: "8px !important",
        },
        arrow: {
          "&:before": {
            backgroundColor: "white",
            border: "1px solid #D4D4D4",
          },
        },
      },
    },

    MuiSwitch: {
      styleOverrides: {
        root: {
          margin: "8px 0px",
          height: "24px",
          padding: "0px",

          "& .MuiSwitch-thumb": {
            width: "100%",
            height: "20px",
          },
          "& .MuiSwitch-switchBase": {
            padding: "0px",
          },
        },

        switchBase: {
          color: "white",
          padding: "0px",
          left: "2px",
          top: "2px",
          width: "20px",

          "&.Mui-checked": {
            color: "white",

            "& .MuiSwitch-track": {
              opacity: "100%",
            },
          },

          "&.Mui-disabled.Mui-checked": {
            color: "white",
            opacity: 1,

            "& + .MuiSwitch-track": {
              backgroundColor: "#8CB8B8",
            },
          },
        },

        track: {
          width: "44px",
          borderRadius: "100px",
          backgroundColor: "#EAEBEC",
          boxShadow: "0px 1px 5px 0px rgba(0, 0, 0, 0.10) inset",
          opacity: "100% !important",
        },

        thumb: {
          boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.10)",
        },

        edgeEnd: {
          borderRadius: "100px",
        },
      },
    },

    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          marginLeft: "0px",
        },
        label: {
          fontWeight: "500",
        },
      },
    },

    MuiAutocomplete: {
      styleOverrides: {
        tag: {
          marginTop: "13px",
          fontWeight: 500,
        },
        inputFocused: {
          fontWeight: 500,
        },
        paper: {
          borderRadius: "12px",
          marginTop: "4px",
          boxShadow: "none",
          border: "1px solid #D4D4D4",
        },
      },
    },
    MuiTab: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiTextField: {
      defaultProps: {
        autoComplete: "off",
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: "#198282",
          fontWeight: 600,
          textDecoration: "none",
          "&:hover": {
            textDecoration: "none",
          },
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 900,
      md: 1099,
      lg: 1100, // This is the only one that's being overridden
      xl: 1536,
    },
  },
  palette: {
    background: {
      default: "#FDFAF7",
    },
    primary: {
      main: "#198282",
      light: "#C8DBD7",
      dark: "#3C5556",
    },
    secondary: {
      main: "#EF7B77",
      light: "#FCDBCD",
      dark: "#C14743",
    },
    error: {
      main: "#C14743",
      light: "#FCDBCD",
      dark: "#C14743",
    },
    success: {
      main: "#468E3E",
      light: "#468E3E",
      dark: "#468E3E",
    },
    text: {
      primary: "#3D3D3D",
      secondary: "#6B6E7B",
      tertiary: "#8E9598",
    },
    border: "#D4D4D4",
    gray: {
      1: "#FAF9FA",
      2: "#F2F2F2",
      3: "#EAEBEC",
      4: "#E2E3E4",
      5: "#D4D4D4",
      6: "#B3AFB6",
      7: "#8E8E98",
      8: "#6B6E7B",
      9: "#3D3D3D",
    },
    teal: {
      1: "#F2F8F8",
      2: "#DEEDED",
      3: "#B6D8D8",
      4: "#8BC1C1",
      5: "#61AAAA",
      6: "#3D9696",
      7: "#198282",
      8: "#146868",
      9: "#0F4E4E",
    },
  },
  mixins: {
    toolbar: {
      minHeight: "50px",
    },
  },
  elevation: ["none", "rgba(34, 32, 29, 0.15) 0px 1px 4px"],
});

export type Elevation = string[];

export default baseTheme;
